import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ExternalLink, FileText, Filter, Loader2, Plus, RefreshCw, Send, Trash2, Wallet, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  adminInputClass,
  filterPillClass,
  filterPillActiveClass,
  StatusPill,
  TableFilterCard,
  TableSkeleton,
} from '@/components/admin/AdminTableStyles';
import AccountingShell, {
  AccountingEmpty,
  AccountingError,
} from '@/components/admin/accounting/AccountingShell';
import LineItemsEditor, { emptyLine } from '@/components/admin/accounting/LineItemsEditor';
import ConfirmDeleteDialog from '@/components/admin/accounting/ConfirmDeleteDialog';
import BulkDeleteDialog, { BulkDeleteBar } from '@/components/admin/accounting/BulkDeleteDialog';
import useRowSelection from '@/hooks/useRowSelection';
import OperatingPaymentsCard from '@/components/admin/OperatingPaymentsCard';
import ChequeEditorDialog from '@/components/admin/ChequeEditorDialog';
import {
  accountingError,
  activeFilterCount,
  billStatusMeta,
  chequeStatusMeta,
  filtersFromSearch,
  formatCents,
  searchFromFilters,
  shortDate,
} from '@/lib/accounting';
import {
  createAccountingBill,
  createAccountingPayee,
  deleteAccountingBill,
  getAccountingBills,
  getAccountingItems,
  getAccountingPayees,
  getAccountingTaxes,
  openAccountingBill,
  recordAccountingBillPayment,
  voidAccountingBill,
} from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';

const DEFAULT_FILTERS = {
  payee: '', status: 'all', cheque: 'all', from: '', to: '', q: '', page: '1',
};
const COUNTED_FILTERS = { payee: '', status: 'all', cheque: 'all', from: '', to: '', q: '' };

// Where a cheque has got to, in the order it gets there. `none` is last
// because "no cheque written" is a different kind of answer from the four
// states a cheque actually passes through.
const CHEQUE_FILTER_VALUES = ['draft', 'issued', 'cleared', 'void', 'none'];

function todayInput() {
  return new Date().toISOString().slice(0, 10);
}

function plusDays(days) {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
}

export default function AccountingBills() {
  const { profile } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [filters, setFilters] = useState(() => filtersFromSearch(location.search, DEFAULT_FILTERS));
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [pageInfo, setPageInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const [payees, setPayees] = useState([]);
  const [items, setItems] = useState([]);
  const [taxes, setTaxes] = useState([]);

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [newPayee, setNewPayee] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busyRef, setBusyRef] = useState('');
  const [deleteFor, setDeleteFor] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  // Deleting a commission payout or an operating cheque removes it from the
  // agency record or from Operating Payments too, so those rows are selectable
  // as well. The server decides: only it knows whether a commission batch has
  // a live cheque written against it.
  const selection = useRowSelection(rows, {
    keyOf: (row) => row.ref,
    canSelect: (row) => row.delete_effect?.allowed,
  });
  const [paymentFor, setPaymentFor] = useState(null);
  const [paymentForm, setPaymentForm] = useState(null);
  const [chequeFor, setChequeFor] = useState(null);
  const [detailFor, setDetailFor] = useState(null);

  const filterCount = activeFilterCount(filters, COUNTED_FILTERS);

  useEffect(() => {
    const search = searchFromFilters(filters, DEFAULT_FILTERS);
    if (search !== location.search) {
      navigate({ pathname: location.pathname, search }, { replace: true });
    }
  }, [filters, location.pathname, location.search, navigate]);

  const load = useCallback(
    async ({ quiet } = {}) => {
      if (quiet) setRefreshing(true);
      else setLoading(true);
      setError('');
      try {
        const { data } = await getAccountingBills({
          payee: filters.payee || undefined,
          status: filters.status === 'all' ? undefined : filters.status,
          cheque_status: filters.cheque === 'all' ? undefined : filters.cheque,
          from: filters.from || undefined,
          to: filters.to || undefined,
          q: filters.q || undefined,
          page: Number(filters.page) || 1,
        });
        setRows(data?.items || []);
        setSummary(data?.summary || null);
        setPageInfo(data?.page_info || null);
      } catch (err) {
        setError(accountingError(err, 'Bills could not be loaded.'));
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [filters]
  );

  useEffect(() => {
    load();
  }, [load]);

  const loadPickers = useCallback(async () => {
    try {
      const [payeeList, itemList, taxList] = await Promise.all([
        getAccountingPayees(),
        getAccountingItems({ context: 'buy', status: 'active', page_size: 100 }),
        getAccountingTaxes(),
      ]);
      setPayees(payeeList.data?.items || []);
      setItems(itemList.data?.items || []);
      setTaxes(taxList.data?.items || []);
    } catch {
      // Pickers degrade to empty; the list still renders its own error state.
    }
  }, []);

  useEffect(() => {
    loadPickers();
  }, [loadPickers]);

  const setFilter = (patch) => setFilters((current) => ({ ...current, ...patch, page: '1' }));

  const openCreate = () => {
    setForm({
      payee_ref: '',
      number: '',
      bill_date: todayInput(),
      due_date: plusDays(30),
      notes: '',
      line_items: [emptyLine()],
    });
    setNewPayee(null);
    setCreateOpen(true);
  };

  const addPayee = async () => {
    if (!newPayee?.name?.trim()) {
      toast.error('The new payee needs a name.');
      return;
    }
    setSaving(true);
    try {
      const { data } = await createAccountingPayee(newPayee);
      await loadPickers();
      setForm((current) => ({ ...current, payee_ref: data.ref }));
      setNewPayee(null);
      toast.success(`${data.name} added as a payee.`);
    } catch (err) {
      toast.error(accountingError(err, 'The payee could not be added.'));
    } finally {
      setSaving(false);
    }
  };

  const submitCreate = async () => {
    if (!form.payee_ref) {
      toast.error('Choose who this bill is payable to.');
      return;
    }
    if (form.line_items.some((line) => !line.description.trim() && !line.item_id)) {
      toast.error('Every line needs a product, a service, or a description.');
      return;
    }
    setSaving(true);
    try {
      await createAccountingBill({
        payee_ref: form.payee_ref,
        number: form.number,
        bill_date: form.bill_date,
        due_date: form.due_date,
        notes: form.notes,
        line_items: form.line_items.map((line) => ({
          item_id: line.item_id || '',
          description: line.description,
          quantity: Number(line.quantity) || 0,
          unit_price: Number(String(line.unit_price).replace(/[$,]/g, '')) || 0,
          tax_ids: line.tax_ids || [],
        })),
      });
      setCreateOpen(false);
      toast.success('Bill created as a draft.');
      await load({ quiet: true });
    } catch (err) {
      toast.error(accountingError(err, 'The bill could not be created.'));
    } finally {
      setSaving(false);
    }
  };

  const runAction = async (bill, action, work, message) => {
    setBusyRef(`${bill.ref}:${action}`);
    try {
      await work();
      toast.success(message);
      await load({ quiet: true });
    } catch (err) {
      toast.error(accountingError(err, 'That action could not be completed.'));
    } finally {
      setBusyRef('');
    }
  };

  /**
   * Open whatever actually stands behind a row.
   *
   * A commission payout or an operating cheque opens the cheque document in the
   * portal's existing cheque editor - the same one the Commissions and
   * Operating Payments workflows use, so there is one cheque view, not a
   * second. Rows with no cheque open a detail panel of the source record.
   */
  const openSource = (bill) => {
    const cheque = bill.source_document?.cheque;
    if (bill.source_document?.available && cheque?.id) {
      setChequeFor(cheque);
      return;
    }
    setDetailFor(bill);
  };

  const openPayment = (bill) => {
    setPaymentFor(bill);
    setPaymentForm({
      amount: (bill.amount_due_cents / 100).toFixed(2),
      payment_date: todayInput(),
      method: '',
      reference: '',
      idempotency_key:
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `billpay-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    });
  };

  const submitPayment = async () => {
    setSaving(true);
    try {
      await recordAccountingBillPayment(paymentFor.ref, {
        amount: Number(paymentForm.amount),
        payment_date: paymentForm.payment_date,
        method: paymentForm.method,
        reference: paymentForm.reference,
        idempotency_key: paymentForm.idempotency_key,
      });
      setPaymentFor(null);
      toast.success('Payment recorded against the bill.');
      await load({ quiet: true });
    } catch (err) {
      toast.error(accountingError(err, 'The payment could not be recorded.'));
    } finally {
      setSaving(false);
    }
  };

  const canCreate = profile?.role === 'admin';
  const canDelete = profile?.is_super_admin === true;

  /**
   * Delete a bill raised here.
   *
   * Never offered on a commission payout or an operating cheque: those are
   * money already paid, owned by the workflow that paid it, and the API
   * refuses them. A bill with payments recorded against it is refused too and
   * voided instead, so the payment history survives.
   */
  const confirmDelete = async () => {
    setBusyRef(`${deleteFor.ref}:delete`);
    try {
      await deleteAccountingBill(deleteFor.ref);
      toast.success('Bill deleted.');
      setDeleteFor(null);
      await load({ quiet: true });
    } catch (err) {
      toast.error(accountingError(err, 'That bill could not be deleted.'));
    } finally {
      setBusyRef('');
    }
  };

  const actions = (
    <>
      <Button type="button" variant="outline" onClick={() => load({ quiet: true })} disabled={refreshing}>
        <RefreshCw className={cn('mr-2 size-4', refreshing && 'animate-spin')} />
        Refresh
      </Button>
      {canCreate && (
        <Button type="button" onClick={openCreate} className="bg-[#062B1A] text-white hover:bg-[#041B10]">
          <Plus className="mr-2 size-4" />
          Create a bill
        </Button>
      )}
    </>
  );

  return (
    <AccountingShell
      title="Bills"
      description="What the college owes. Bills raised here sit alongside the commission payouts and operating cheques the portal already records, so the payables picture is complete without any obligation being counted twice."
      actions={actions}
    >
      {summary && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
            <CardContent className="p-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[#062B1A]/45">
                Outstanding
              </p>
              <p className="mt-2 text-2xl font-semibold text-[#062B1A]">{summary.outstanding_display}</p>
            </CardContent>
          </Card>
          <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
            <CardContent className="p-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[#062B1A]/45">
                Overdue
              </p>
              <p className="mt-2 text-2xl font-semibold text-red-700">{summary.overdue_display}</p>
            </CardContent>
          </Card>
          {/* A cheque handed over but not yet taken from the account is money
              the college has committed and still holds. The bill reads Paid
              and the bank does not know it yet; both facts are true, and this
              is the one the payables page could not previously answer. */}
          <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
            <CardContent className="p-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[#062B1A]/45">
                Issued, not cleared
              </p>
              <p className="mt-2 text-2xl font-semibold text-amber-700">
                {summary.issued_not_cleared_display}
              </p>
              {summary.issued_not_cleared_count > 0 && (
                <button
                  type="button"
                  onClick={() => setFilter({ cheque: 'issued' })}
                  className="mt-1 text-[11px] font-medium text-[#062B1A]/50 underline-offset-2 hover:underline"
                >
                  {summary.issued_not_cleared_count} cheque
                  {summary.issued_not_cleared_count === 1 ? '' : 's'} outstanding
                </button>
              )}
            </CardContent>
          </Card>
          <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
            <CardContent className="p-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[#062B1A]/45">
                Bills shown
              </p>
              <p className="mt-2 text-2xl font-semibold text-[#062B1A]">{summary.bill_count}</p>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setShowFilters((open) => !open)}
          className={cn(filterPillClass, filterCount > 0 && filterPillActiveClass)}
        >
          <Filter className="mr-1.5 size-3.5" />
          Filters{filterCount > 0 ? ` (${filterCount})` : ''}
        </Button>
        {filterCount > 0 && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setFilters(DEFAULT_FILTERS)}
            className="h-8 px-2 text-xs text-[#062B1A]/60"
          >
            <X className="mr-1 size-3.5" />
            Clear
          </Button>
        )}
      </div>

      {showFilters && (
        <TableFilterCard>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <Label htmlFor="bill-payee">Vendor or payee</Label>
              <select
                id="bill-payee"
                value={filters.payee}
                onChange={(event) => setFilter({ payee: event.target.value })}
                className={cn('mt-1 h-10 w-full rounded-md border bg-white px-3 text-sm', adminInputClass)}
              >
                <option value="">All payees</option>
                {payees.map((payee) => (
                  <option key={payee.ref} value={payee.ref}>
                    {payee.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="bill-status">Status</Label>
              <select
                id="bill-status"
                value={filters.status}
                onChange={(event) => setFilter({ status: event.target.value })}
                className={cn('mt-1 h-10 w-full rounded-md border bg-white px-3 text-sm', adminInputClass)}
              >
                <option value="all">Any status</option>
                {['draft', 'open', 'partial', 'paid', 'overdue', 'void'].map((status) => (
                  <option key={status} value={status}>
                    {billStatusMeta(status).label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="bill-cheque-status">Cheque</Label>
              <select
                id="bill-cheque-status"
                value={filters.cheque}
                onChange={(event) => setFilter({ cheque: event.target.value })}
                className={cn('mt-1 h-10 w-full rounded-md border bg-white px-3 text-sm', adminInputClass)}
              >
                <option value="all">Any cheque state</option>
                {CHEQUE_FILTER_VALUES.map((value) => (
                  <option key={value} value={value}>
                    {chequeStatusMeta(value).label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="bill-from">Bill date from</Label>
              <Input
                id="bill-from"
                type="date"
                value={filters.from}
                onChange={(event) => setFilter({ from: event.target.value })}
                className={cn('mt-1', adminInputClass)}
              />
            </div>
            <div>
              <Label htmlFor="bill-to">Bill date to</Label>
              <Input
                id="bill-to"
                type="date"
                value={filters.to}
                onChange={(event) => setFilter({ to: event.target.value })}
                className={cn('mt-1', adminInputClass)}
              />
            </div>
            <div>
              <Label htmlFor="bill-search">Search</Label>
              <Input
                id="bill-search"
                type="search"
                value={filters.q}
                onChange={(event) => setFilter({ q: event.target.value })}
                placeholder="Number or note"
                className={cn('mt-1', adminInputClass)}
              />
            </div>
          </div>
        </TableFilterCard>
      )}

      {canDelete && (
        <BulkDeleteBar
          count={selection.count}
          noun="bill"
          onDelete={() => setBulkOpen(true)}
          onClear={selection.clear}
        />
      )}

      {error ? (
        <AccountingError message={error} onRetry={load} />
      ) : loading ? (
        <TableSkeleton rows={6} />
      ) : rows.length === 0 ? (
        <AccountingEmpty
          title={filterCount > 0 ? 'No bills match these filters' : 'No bills yet'}
          description={
            filterCount > 0
              ? 'Clear a filter to widen the search.'
              : 'Raise a bill for something the college owes, or record it in the workflow that already handles it.'
          }
          action={
            filterCount > 0 ? (
              <Button type="button" variant="outline" onClick={() => setFilters(DEFAULT_FILTERS)}>
                Clear filters
              </Button>
            ) : canCreate ? (
              <Button type="button" onClick={openCreate} className="bg-[#062B1A] text-white">
                <Plus className="mr-2 size-4" />
                Create a bill
              </Button>
            ) : null
          }
        />
      ) : (
        <Card className="overflow-hidden border border-[rgba(6,43,26,0.08)] shadow-sm">
          <div className="min-w-0 overflow-x-auto">
            <table className="w-full min-w-[1190px] text-left text-sm">
              <thead className="bg-[#062B1A] text-[10px] uppercase tracking-[0.035em] text-white">
                <tr>
                  {canDelete && (
                    <th className="w-10 px-3 py-2.5">
                      <Checkbox
                        aria-label="Select every bill on this page that can be deleted"
                        className="border-white/50 data-[state=checked]:border-white data-[state=checked]:bg-white data-[state=checked]:text-[#062B1A]"
                        checked={selection.allSelected}
                        disabled={selection.selectableCount === 0}
                        onCheckedChange={selection.toggleAll}
                      />
                    </th>
                  )}
                  <th className="px-3 py-2.5 font-semibold">Status</th>
                  <th className="px-3 py-2.5 font-semibold">Cheque</th>
                  <th className="px-3 py-2.5 font-semibold">Payee</th>
                  <th className="px-3 py-2.5 font-semibold">Bill date</th>
                  <th className="px-3 py-2.5 font-semibold">Due</th>
                  <th className="px-3 py-2.5 font-semibold">Reference</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Total</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Amount due</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[rgba(6,43,26,0.07)]">
                {rows.map((bill) => {
                  const meta = billStatusMeta(bill.status);
                  const cheque = chequeStatusMeta(bill.cheque_status);
                  // Which date the cheque's current state is actually about.
                  // Showing "issued" over the date it cleared, or vice versa,
                  // would be worse than showing no date at all.
                  const chequeDate = shortDate(
                    bill.cheque_status === 'cleared'
                      ? bill.cheque?.cleared_at
                      : bill.cheque_status === 'void'
                        ? bill.cheque?.voided_at
                        : bill.cheque_status === 'issued'
                          ? bill.cheque?.issued_at
                          : ''
                  );
                  const busy = busyRef.startsWith(bill.ref);
                  return (
                    <tr key={bill.ref} className="bg-white hover:bg-[#F7F8FA]/80">
                      {canDelete && (
                        <td className="px-3 py-3">
                          <Checkbox
                            aria-label={`Select ${bill.payee?.name || 'this bill'}`}
                            checked={selection.isSelected(bill)}
                            disabled={!bill.delete_effect?.allowed}
                            title={bill.delete_effect?.reason || undefined}
                            onCheckedChange={() => selection.toggle(bill)}
                          />
                        </td>
                      )}
                      <td className="px-3 py-3">
                        <StatusPill tone={meta.tone}>{meta.label}</StatusPill>
                      </td>
                      <td className="px-3 py-3">
                        <span
                          title={
                            bill.cheque
                              ? [
                                  cheque.meaning,
                                  bill.cheque.reference ? `Cheque ${bill.cheque.reference}` : '',
                                  bill.cheque.status_updated_by_name
                                    ? `Last moved by ${bill.cheque.status_updated_by_name}`
                                    : '',
                                  bill.cheque.void_reason ? `Reason: ${bill.cheque.void_reason}` : '',
                                ]
                                  .filter(Boolean)
                                  .join(' · ')
                              : cheque.meaning
                          }
                        >
                          <StatusPill tone={cheque.tone}>{cheque.label}</StatusPill>
                        </span>
                        {chequeDate && (
                          <div className="mt-0.5 text-[11px] text-[#062B1A]/40">{chequeDate}</div>
                        )}
                        {bill.cheque?.reference && !chequeDate && (
                          <div className="mt-0.5 font-mono text-[11px] text-[#062B1A]/40">
                            {bill.cheque.reference}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        {bill.payee.link ? (
                          <Link
                            to={bill.payee.link}
                            className="text-sm font-medium text-[#062B1A] hover:underline"
                          >
                            {bill.payee.name}
                          </Link>
                        ) : (
                          <span className="text-sm text-[#062B1A]">{bill.payee.name}</span>
                        )}
                        <div className="text-[11px] text-[#062B1A]/40">{bill.origin_label}</div>
                      </td>
                      <td className="px-3 py-3 text-xs text-[#062B1A]/70">{bill.bill_date || '—'}</td>
                      <td className="px-3 py-3">
                        {bill.due_phrase ? (
                          <>
                            <div
                              className={cn(
                                'text-xs font-medium',
                                bill.status === 'overdue' ? 'text-red-700' : 'text-[#062B1A]/70'
                              )}
                            >
                              {bill.due_phrase}
                            </div>
                            <div className="text-[11px] text-[#062B1A]/40">{bill.due_date || '—'}</div>
                          </>
                        ) : (
                          <div className="text-xs text-[#062B1A]/70">{bill.due_date || '—'}</div>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <span className="font-mono text-xs text-[#062B1A]">{bill.number || '—'}</span>
                        {bill.notes && (
                          <div className="text-[11px] text-[#062B1A]/40">{bill.notes}</div>
                        )}
                      </td>
                      <td className="px-3 py-3 text-right text-xs text-[#062B1A]/70">
                        {bill.total_display}
                      </td>
                      <td className="px-3 py-3 text-right text-sm font-semibold text-[#062B1A]">
                        {bill.amount_due_display}
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex flex-wrap items-center justify-end gap-1.5">
                          {bill.payable && bill.status === 'draft' && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              disabled={busy}
                              onClick={() =>
                                runAction(bill, 'open', () => openAccountingBill(bill.ref), 'Bill opened.')
                              }
                            >
                              <Send className="mr-1.5 size-3.5" />
                              Open
                            </Button>
                          )}
                          {bill.payable && ['open', 'partial', 'overdue'].includes(bill.status) && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              disabled={busy}
                              onClick={() => openPayment(bill)}
                            >
                              <Wallet className="mr-1.5 size-3.5" />
                              Record payment
                            </Button>
                          )}
                          {/* Always offered, on every row. A projected payable
                              opens the cheque that paid it; anything without a
                              cheque opens the underlying record instead. It is
                              never a link to the page you are already on. */}
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => openSource(bill)}
                            title={
                              bill.source_document?.available
                                ? bill.source_document.label
                                : bill.source_document?.reason || 'Open the underlying record'
                            }
                          >
                            {bill.source_document?.available ? (
                              <FileText className="mr-1.5 size-3.5" />
                            ) : (
                              <ExternalLink className="mr-1.5 size-3.5" />
                            )}
                            {bill.source_document?.available ? 'Open cheque' : 'Open source record'}
                          </Button>
                          {profile?.is_super_admin && bill.payable && bill.status !== 'void' && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              disabled={busy}
                              className="text-red-700 hover:bg-red-50"
                              onClick={() =>
                                runAction(bill, 'void', () => voidAccountingBill(bill.ref), 'Bill voided.')
                              }
                            >
                              Void
                            </Button>
                          )}
                          {canDelete && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              disabled={busy || !bill.delete_effect?.allowed}
                              className="text-red-700 hover:bg-red-50"
                              title={
                                bill.delete_effect?.allowed
                                  ? bill.delete_effect.cascade || 'Delete this bill'
                                  : bill.delete_effect?.reason || 'This bill cannot be deleted'
                              }
                              onClick={() => setDeleteFor(bill)}
                            >
                              <Trash2 className="size-3.5" />
                              <span className="sr-only">Delete</span>
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {pageInfo && pageInfo.total_pages > 1 && (
            <div className="flex items-center justify-between border-t border-[rgba(6,43,26,0.07)] bg-white px-4 py-3 text-xs text-[#062B1A]/60">
              <span>{pageInfo.total_count} bills</span>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={pageInfo.current_page <= 1}
                  onClick={() => setFilters((current) => ({ ...current, page: String(pageInfo.current_page - 1) }))}
                >
                  Previous
                </Button>
                <span>
                  Page {pageInfo.current_page} of {pageInfo.total_pages}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={pageInfo.current_page >= pageInfo.total_pages}
                  onClick={() => setFilters((current) => ({ ...current, page: String(pageInfo.current_page + 1) }))}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* The cheque-based operating-payment workflow. Its records appear as
          already-paid rows in the table above; this is where they are created
          and where their cheque is written, so the workflow stays in one place
          rather than being half-rebuilt as a bill. */}
      <div className="pt-2">
        <OperatingPaymentsCard onChanged={() => load({ quiet: true })} />
      </div>

      {/* The portal's existing cheque document view, reused rather than
          rebuilt: it renders, edits and downloads the same cheque the
          Commissions and Operating Payments workflows produce. */}
      {chequeFor && (
        <ChequeEditorDialog
          cheque={chequeFor}
          onClose={() => setChequeFor(null)}
          onSaved={() => load({ quiet: true })}
          onDeleted={() => {
            setChequeFor(null);
            load({ quiet: true });
          }}
        />
      )}

      {/* For a payable with no cheque behind it, the source record itself. */}
      <Dialog open={Boolean(detailFor)} onOpenChange={(open) => !open && setDetailFor(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{detailFor?.payee?.name || 'Source record'}</DialogTitle>
            <DialogDescription>
              {detailFor?.origin_label}
              {detailFor?.source_document?.reason ? ` · ${detailFor.source_document.reason}` : ''}
            </DialogDescription>
          </DialogHeader>
          {detailFor && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs uppercase tracking-[0.1em] text-[#062B1A]/45">Bill date</p>
                  <p className="mt-0.5 text-[#062B1A]">{detailFor.bill_date || '—'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.1em] text-[#062B1A]/45">Reference</p>
                  <p className="mt-0.5 font-mono text-[#062B1A]">{detailFor.number || '—'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.1em] text-[#062B1A]/45">Total</p>
                  <p className="mt-0.5 font-semibold text-[#062B1A]">{detailFor.total_display}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.1em] text-[#062B1A]/45">Amount due</p>
                  <p className="mt-0.5 font-semibold text-[#062B1A]">{detailFor.amount_due_display}</p>
                </div>
              </div>

              {/* Where the cheque got to, and who moved it there. A payable
                  can read Paid while its cheque has not yet been presented at
                  the bank, so the two are reported separately rather than
                  collapsed into one word. */}
              <div className="rounded-lg border border-[#062B1A]/10 p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-xs uppercase tracking-[0.1em] text-[#062B1A]/45">Cheque</p>
                  <StatusPill tone={chequeStatusMeta(detailFor.cheque_status).tone}>
                    {chequeStatusMeta(detailFor.cheque_status).label}
                  </StatusPill>
                  {detailFor.cheque?.reference && (
                    <span className="font-mono text-xs text-[#062B1A]/55">
                      {detailFor.cheque.reference}
                    </span>
                  )}
                </div>
                <p className="mt-1 text-xs text-[#062B1A]/55">
                  {detailFor.cheque
                    ? chequeStatusMeta(detailFor.cheque_status).meaning
                    : detailFor.source_document?.reason
                      || chequeStatusMeta('none').meaning}
                </p>
                {detailFor.cheque?.void_reason && (
                  <p className="mt-1 text-xs text-red-700">
                    Voided: {detailFor.cheque.void_reason}
                  </p>
                )}
                {detailFor.cheque?.status_history?.length > 0 && (
                  <ul className="mt-2 space-y-1 border-t border-[#062B1A]/8 pt-2">
                    {detailFor.cheque.status_history.map((entry, index) => (
                      <li key={index} className="flex flex-wrap gap-x-2 text-[11px] text-[#062B1A]/55">
                        <span className="font-medium text-[#062B1A]">
                          {chequeStatusMeta(entry.to).label}
                        </span>
                        <span>{shortDate(entry.changed_at)}</span>
                        {entry.changed_by_name && <span>· {entry.changed_by_name}</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="overflow-hidden rounded-lg border border-[#062B1A]/10">
                <table className="w-full text-left text-xs">
                  <thead className="bg-[#F7F8FA] uppercase text-[#062B1A]/50">
                    <tr>
                      <th className="px-3 py-2">Description</th>
                      <th className="px-3 py-2 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#062B1A]/8">
                    {(detailFor.line_items || []).map((line, index) => (
                      <tr key={index}>
                        <td className="px-3 py-2 text-[#062B1A]">{line.description || '—'}</td>
                        <td className="px-3 py-2 text-right text-[#062B1A]">
                          {formatCents(line.amount_cents)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {detailFor.payee?.link && (
                <Button asChild variant="outline" size="sm">
                  <Link to={detailFor.payee.link}>
                    <ExternalLink className="mr-1.5 size-3.5" />
                    Open {detailFor.payee.name}
                  </Link>
                </Button>
              )}
              {!detailFor.payee?.link && detailFor.payee?.source_label === 'Removed' && (
                <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  The payee record for this payment has since been deleted. The payment itself is
                  kept here so the history stays complete.
                </p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDetailFor(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>Create a bill</DialogTitle>
            <DialogDescription>
              Payable to an existing agency, or to a vendor you add here. There is no separate
              Vendors page — agencies are referenced from their own records rather than copied.
            </DialogDescription>
          </DialogHeader>
          {form && (
            <div className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label htmlFor="bill-payee-select">Payee</Label>
                  <select
                    id="bill-payee-select"
                    value={form.payee_ref}
                    onChange={(event) => setForm({ ...form, payee_ref: event.target.value })}
                    className={cn('mt-1 h-10 w-full rounded-md border bg-white px-3 text-sm', adminInputClass)}
                  >
                    <option value="">Choose a payee…</option>
                    {payees.map((payee) => (
                      <option key={payee.ref} value={payee.ref}>
                        {payee.name} ({payee.source_label})
                      </option>
                    ))}
                  </select>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="mt-1 h-7 px-1 text-xs"
                    onClick={() => setNewPayee(newPayee ? null : { name: '', email: '', address: '' })}
                  >
                    <Plus className="mr-1 size-3" />
                    {newPayee ? 'Cancel new payee' : 'Add a new payee'}
                  </Button>
                </div>
                <div>
                  <Label htmlFor="bill-number">Bill / reference number</Label>
                  <Input
                    id="bill-number"
                    value={form.number}
                    onChange={(event) => setForm({ ...form, number: event.target.value })}
                    className={cn('mt-1', adminInputClass)}
                  />
                </div>
                <div>
                  <Label htmlFor="bill-date-input">Bill date</Label>
                  <Input
                    id="bill-date-input"
                    type="date"
                    value={form.bill_date}
                    onChange={(event) => setForm({ ...form, bill_date: event.target.value })}
                    className={cn('mt-1', adminInputClass)}
                  />
                </div>
                <div>
                  <Label htmlFor="bill-due-input">Due date</Label>
                  <Input
                    id="bill-due-input"
                    type="date"
                    value={form.due_date}
                    onChange={(event) => setForm({ ...form, due_date: event.target.value })}
                    className={cn('mt-1', adminInputClass)}
                  />
                </div>
              </div>

              {newPayee && (
                <div className="grid gap-2 rounded-lg border border-[#062B1A]/10 bg-[#F7F8FA]/60 p-3 sm:grid-cols-[1fr_1fr_1fr_auto]">
                  <Input
                    aria-label="New payee name"
                    value={newPayee.name}
                    onChange={(event) => setNewPayee({ ...newPayee, name: event.target.value })}
                    placeholder="Payee name"
                    className={adminInputClass}
                  />
                  <Input
                    aria-label="New payee email"
                    value={newPayee.email}
                    onChange={(event) => setNewPayee({ ...newPayee, email: event.target.value })}
                    placeholder="Email"
                    className={adminInputClass}
                  />
                  <Input
                    aria-label="New payee address"
                    value={newPayee.address}
                    onChange={(event) => setNewPayee({ ...newPayee, address: event.target.value })}
                    placeholder="Address"
                    className={adminInputClass}
                  />
                  <Button type="button" onClick={addPayee} disabled={saving} className="bg-[#062B1A] text-white">
                    Add
                  </Button>
                </div>
              )}

              <LineItemsEditor
                lines={form.line_items}
                onChange={(line_items) => setForm({ ...form, line_items })}
                items={items}
                taxes={taxes}
                disabled={saving}
              />

              <div>
                <Label htmlFor="bill-notes">Notes</Label>
                <Input
                  id="bill-notes"
                  value={form.notes}
                  onChange={(event) => setForm({ ...form, notes: event.target.value })}
                  className={cn('mt-1', adminInputClass)}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={submitCreate}
              disabled={saving}
              className="bg-[#062B1A] text-white hover:bg-[#041B10]"
            >
              {saving && <Loader2 className="mr-2 size-4 animate-spin" />}
              Create draft
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BulkDeleteDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        noun="bill"
        rows={selection.selectedRows}
        labelOf={(row) => `${row.payee?.name} · ${row.number || 'no reference'} · ${row.total_display}`}
        cascadeOf={(row) => row.delete_effect?.cascade}
        remove={(row) => deleteAccountingBill(row.ref)}
        onFinished={() => {
          selection.clear();
          load({ quiet: true });
        }}
      />

      <ConfirmDeleteDialog
        open={Boolean(deleteFor)}
        onOpenChange={(next) => !next && setDeleteFor(null)}
        title="Delete this bill?"
        detail={
          deleteFor ? `${deleteFor.payee?.name} · ${deleteFor.number || 'no reference'} · ${deleteFor.total_display}` : ''
        }
        consequence={deleteFor?.delete_effect?.cascade || ''}
        confirmLabel="Delete bill"
        busy={busyRef.endsWith(':delete')}
        onConfirm={confirmDelete}
      />

      <Dialog open={Boolean(paymentFor)} onOpenChange={(open) => !open && setPaymentFor(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Record a bill payment</DialogTitle>
            <DialogDescription>
              {paymentFor && `${paymentFor.payee.name} · ${paymentFor.amount_due_display} still owed`}
            </DialogDescription>
          </DialogHeader>
          {paymentForm && (
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="bill-payment-amount">Amount</Label>
                <Input
                  id="bill-payment-amount"
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={paymentForm.amount}
                  onChange={(event) => setPaymentForm({ ...paymentForm, amount: event.target.value })}
                  className={cn('mt-1', adminInputClass)}
                />
              </div>
              <div>
                <Label htmlFor="bill-payment-date">Payment date</Label>
                <Input
                  id="bill-payment-date"
                  type="date"
                  value={paymentForm.payment_date}
                  onChange={(event) => setPaymentForm({ ...paymentForm, payment_date: event.target.value })}
                  className={cn('mt-1', adminInputClass)}
                />
              </div>
              <div>
                <Label htmlFor="bill-payment-method">Method</Label>
                <Input
                  id="bill-payment-method"
                  value={paymentForm.method}
                  onChange={(event) => setPaymentForm({ ...paymentForm, method: event.target.value })}
                  placeholder="EFT, cheque"
                  className={cn('mt-1', adminInputClass)}
                />
              </div>
              <div>
                <Label htmlFor="bill-payment-reference">Reference</Label>
                <Input
                  id="bill-payment-reference"
                  value={paymentForm.reference}
                  onChange={(event) => setPaymentForm({ ...paymentForm, reference: event.target.value })}
                  className={cn('mt-1', adminInputClass)}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setPaymentFor(null)}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={submitPayment}
              disabled={saving}
              className="bg-[#062B1A] text-white hover:bg-[#041B10]"
            >
              {saving && <Loader2 className="mr-2 size-4 animate-spin" />}
              Record payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AccountingShell>
  );
}
